import React, { useState, useEffect, useCallback } from 'react';
import { Box, Alert } from '@mui/material';
import { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import FilterBar from './FilterBar';
import StatCardsGrid from './StatCardsGrid';
import OverviewDetailsTable from './OverviewDetailsTable';

const MAIN_API_URL = '/db/overview.php';
const DETAILS_API_URL = '/db/overview_details_api.php';

const getInitialDates = () => [new DateObject({ calendar: persian }).set('day', 1), new DateObject({ calendar: persian })];

// !! تابع جدید و کلیدی برای ترجمه اعداد فارسی به انگلیسی !!
const toEnglishDigits = (str) => {
    if (!str) return str;
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    const englishDigits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
    let newStr = String(str);
    for (let i = 0; i < 10; i++) {
        newStr = newStr.replace(new RegExp(persianDigits[i], 'g'), englishDigits[i]);
    }
    return newStr;
};


const Overview = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState({ current: null, previous: null });
    const [filters, setFilters] = useState({ dates: getInitialDates(), compare: false });
    const [activeStat, setActiveStat] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsData, setDetailsData] = useState(null);
    const [detailsError, setDetailsError] = useState(null);

    const makeApiRequest = useCallback(async (url, action, extraParams = {}) => {
        const params = new URLSearchParams({ action, ...extraParams });
        if (filters.dates && filters.dates.length === 2) {
            const [after, before] = filters.dates;

            // 1. تاریخ به صورت شمسی فرمت می‌شود
            const afterDateStr = after.format("YYYY-MM-DD");
            const beforeDateStr = before.format("YYYY-MM-DD");

            // 2. اعداد تاریخ به انگلیسی ترجمه و سپس به پارامترها اضافه می‌شود
            params.append('after', toEnglishDigits(afterDateStr));
            params.append('before', toEnglishDigits(beforeDateStr));

            if (action === 'get_main_stats' && filters.compare) {
                const diff = before.diff(after, 'days');
                const prev_before = new DateObject(after).subtract(1, 'day');
                const prev_after = new DateObject(prev_before).subtract(diff, 'days');
                
                const compareAfterStr = prev_after.format("YYYY-MM-DD");
                const compareBeforeStr = prev_before.format("YYYY-MM-DD");
                
                params.append('compare_after', toEnglishDigits(compareAfterStr));
                params.append('compare_before', toEnglishDigits(compareBeforeStr));
            }
        }
        try {
            const response = await fetch(`${url}?${params.toString()}`);
            if (!response.ok) throw new Error(`خطای شبکه: ${response.status}`);
            const payload = await response.json();
            if (!payload.success) throw new Error(payload.message || 'خطایی در سرور رخ داد.');
            return payload.data;
        } catch (err) {
            if (err instanceof SyntaxError) {
                throw new Error("پاسخ دریافتی از سرور معتبر نیست.");
            }
            throw err;
        }
    }, [filters]);

    useEffect(() => {
        const fetchDashboardData = async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await makeApiRequest(MAIN_API_URL, 'get_main_stats');
                setData(result);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchDashboardData();
    }, [makeApiRequest]);

    useEffect(() => {
        if (!activeStat) return;
        const fetchDetailedData = async () => {
            setDetailsLoading(true);
            setDetailsData(null);
            setDetailsError(null);
            try {
                const result = await makeApiRequest(DETAILS_API_URL, 'get_details', { type: activeStat.key });
                setDetailsData(result);
            } catch (err) {
                setDetailsError(err.message);
            } finally {
                setDetailsLoading(false);
            }
        };
        fetchDetailedData();
    }, [activeStat, makeApiRequest]);

    const handleCardClick = (card) => {
        setActiveStat(current => (current && current.key === card.key ? null : card));
    };

    return (
        <Box sx={{ direction: 'rtl' }}>
            <FilterBar 
                initialFilters={filters}
                onFilterChange={setFilters} 
            />
            
            {error && <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(255,0,0,0.3)', color: '#fff' }}>{error}</Alert>}
            
            <StatCardsGrid 
                loading={loading}
                data={data}
                onCardClick={handleCardClick}
            />
            
            {activeStat && (
                <OverviewDetailsTable
                    key={activeStat.key}
                    statInfo={activeStat}
                    loading={detailsLoading}
                    data={detailsData}
                    error={detailsError}
                    onClose={() => setActiveStat(null)}
                />
            )}
        </Box>
    );
};

export default Overview;